/**
 * A job from accepting it to done: where to go (handing off to the driver's navigation
 * app), the passenger (calling them with the number withheld), the fare, and the next
 * step: arrived, passenger on board, complete (collecting the cash on cash jobs). Also
 * shows jobs in the driver's queue, and finished ones.
 */
import { ApiError, errorMessage, get, post } from "@taxi/mobile-web/api";
import { miles, money, when } from "@taxi/mobile-web/format";
import { useResource } from "@taxi/mobile-web/hooks";
import { BagIcon, CashIcon, NavigateIcon, PhoneIcon, UsersIcon } from "@taxi/mobile-web/icons";
import { MapView } from "@taxi/mobile-web/map";
import { useRealtime } from "@taxi/mobile-web/realtime";
import { navigate } from "@taxi/mobile-web/router";
import {
  Avatar,
  Button,
  Card,
  Empty,
  Loading,
  Notice,
  Sheet,
  Steps,
  TopBar,
  useToast,
} from "@taxi/mobile-web/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { PaymentTag, placeParts, TripRoute } from "../components/parts";
import { directions, withheldCall } from "../lib/device";
import { useDriver } from "../lib/driver";
import { metresBetween } from "../lib/location";
import type { DriverJob } from "../lib/types";

type Step = "arrived" | "on-board" | "no-show" | "complete" | "cash-collected";

const QUEUED: DriverJob["status"][] = ["scheduled", "dispatching", "offered"];

export function JobScreen({ id }: { id: string }) {
  const job = useResource(() => get<DriverJob>(`/driver/jobs/${id}`), [id]);
  const { location, refresh } = useDriver();
  const toast = useToast();
  const [busy, setBusy] = useState<Step | null>(null);
  const [sheet, setSheet] = useState<"navigate" | "complete" | "no-show" | null>(null);

  useRealtime((event) => {
    if ((event.type === "job.updated" && event.jobId === id) || event.type === "reconnected") {
      void job.reload();
    }
  });

  // Cancelled while the driver's looking at it: make sure they notice.
  const j = job.data;
  const lastStatus = useRef<string | null>(null);
  useEffect(() => {
    if (!j) return;
    if (lastStatus.current && lastStatus.current !== j.status && j.status === "cancelled") {
      toast("This job has been cancelled", "error");
      navigator.vibrate?.([250, 100, 250]);
    }
    lastStatus.current = j.status;
  }, [j, toast]);

  if (job.error) {
    return (
      <div className="screen">
        <TopBar title="Job" back="/" />
        <Empty title="Can't show this job">
          <p>
            {job.error instanceof ApiError && job.error.status === 404
              ? "It isn't one of yours any more."
              : errorMessage(job.error)}
          </p>
          <Button onClick={() => navigate("/", { replace: true })}>Back to driving</Button>
        </Empty>
      </div>
    );
  }
  if (!j) return <Loading />;

  async function step(path: Step, body?: unknown) {
    setBusy(path);
    try {
      const updated = await post<DriverJob>(`/driver/jobs/${id}/${path}`, body);
      job.setData(updated);
      setSheet(null);
      if (path === "complete" && updated.paymentMethod === "cash" && !updated.cashCollected) {
        navigator.vibrate?.(200);
      }
      void refresh();
    } catch (error) {
      toast(errorMessage(error), "error");
      void job.reload();
    } finally {
      setBusy(null);
    }
  }

  const fix = location.fix;
  const active = j.status === "accepted" || j.status === "arrived" || j.status === "on_board";
  const toDropoff = j.status === "on_board";
  const target = toDropoff ? j.dropoff : j.pickup;
  const away = fix && active ? miles(metresBetween(fix, target)) : null;
  const fare = j.finalFarePence ?? j.pricePence;
  const name = j.customer.name;
  const cashDue = j.status === "completed" && j.paymentMethod === "cash" && !j.cashCollected;

  const heading: Record<string, [string, ReactNode]> = {
    accepted: [
      "Head to the pickup",
      away ? `${away} away. Picking up ${name}.` : `Picking up ${name}.`,
    ],
    arrived: ["You're at the pickup", `Waiting for ${name}. Call them if they're not out soon.`],
    on_board: [
      "On the way to the destination",
      j.stops.length
        ? `${j.stops.length} stop${j.stops.length === 1 ? "" : "s"} on the way.`
        : `Taking ${name} to ${placeParts(j.dropoff).title}.`,
    ],
    completed: cashDue
      ? [`Collect ${money(j.amountToCollectPence)}`, `Take the fare in cash from ${name}.`]
      : ["Trip complete", "Nicely done."],
    no_show: ["No-show", `${name} didn't come out. They haven't been charged.`],
    cancelled: ["Cancelled", "This job was cancelled. You're free for the next one."],
  };
  const [title, detail] = heading[j.status] ?? [
    `Booked for ${when(new Date(j.pickupAt))}`,
    "It's yours: it'll be offered to you first when it's due.",
  ];

  let actions: ReactNode = null;
  if (j.nextStep === "arrived") {
    actions = (
      <Button className="btn-hero" loading={busy === "arrived"} onClick={() => step("arrived")}>
        I've arrived
      </Button>
    );
  } else if (j.nextStep === "on_board") {
    actions = (
      <>
        <Button className="btn-hero" loading={busy === "on-board"} onClick={() => step("on-board")}>
          Passenger on board
        </Button>
        {j.canNoShow && (
          <Button variant="ghost" className="danger-text" onClick={() => setSheet("no-show")}>
            They haven't come out
          </Button>
        )}
      </>
    );
  } else if (j.nextStep === "complete") {
    actions = (
      <Button className="btn-hero" onClick={() => setSheet("complete")}>
        Complete trip
      </Button>
    );
  } else if (cashDue) {
    actions = (
      <Button
        className="btn-hero"
        loading={busy === "cash-collected"}
        onClick={() => step("cash-collected")}
      >
        I've collected {money(j.amountToCollectPence)}
      </Button>
    );
  } else if (!QUEUED.includes(j.status)) {
    actions = <Button onClick={() => navigate("/", { replace: true })}>Back to driving</Button>;
  }

  return (
    <div className="job">
      <TopBar back="/" transparent />
      <MapView
        className="job-map"
        driver={fix && active ? { lat: fix.lat, lng: fix.lng, heading: fix.heading } : null}
        carLabel="You"
        pickup={j.pickup}
        stops={j.stops}
        dropoff={j.dropoff}
      />
      <div className="job-sheet">
        {JOB_STEPS[j.status] !== undefined && (
          <Steps steps={STEPS} current={JOB_STEPS[j.status]!} />
        )}
        <div className="job-head">
          <span>Job {j.number}</span>
          <PaymentTag method={j.paymentMethod} />
        </div>
        {cashDue ? (
          <section className="collect-hero">
            <span className="collect-icon" aria-hidden="true">
              <CashIcon size={28} />
            </span>
            <span className="overline">Collect in cash</span>
            <strong>{money(j.amountToCollectPence)}</strong>
            <p>From {name}, then tap below.</p>
          </section>
        ) : (
          <div className="job-title">
            <h2>{title}</h2>
            <p>{detail}</p>
          </div>
        )}

        {active && (
          <Card className="next-place">
            <span className="overline">{toDropoff ? "Destination" : "Pickup"}</span>
            <strong>{placeParts(target).title}</strong>
            {placeParts(target).rest && <span>{placeParts(target).rest}</span>}
            {target.notes && <p className="place-notes">{target.notes}</p>}
            <Button
              variant="secondary"
              className="directions-button"
              onClick={() => setSheet("navigate")}
            >
              <NavigateIcon size={18} /> Directions
              {away && <span className="pill">{away}</span>}
            </Button>
          </Card>
        )}

        <Card className="passenger">
          <div className="passenger-head">
            <Avatar name={name} size={48} />
            <div>
              <strong>{name}</strong>
              <span className="meta">
                <span>
                  <UsersIcon size={14} /> {j.passengers}
                </span>
                {j.luggage > 0 && (
                  <span>
                    <BagIcon size={14} /> {j.luggage}
                  </span>
                )}
                {j.vehicleTypeName && <span>{j.vehicleTypeName}</span>}
              </span>
            </div>
            {active && j.customer.phone && (
              <a
                className="icon-btn call-button"
                href={withheldCall(j.customer.phone)}
                aria-label={`Call ${name}`}
              >
                <PhoneIcon size={20} />
              </a>
            )}
          </div>
          {j.notes && <p className="job-notes">“{j.notes}”</p>}
          {j.accountName && <p className="fine-print">On account: {j.accountName}</p>}
          {active && j.customer.phone && (
            <p className="fine-print">Calls go with your number withheld.</p>
          )}
        </Card>

        <Card className="fare">
          <div className="fare-row">
            <span className="overline">
              {j.status === "completed" ? "Fare" : j.isEstimate ? "Fare (estimate)" : "Fare"}
            </span>
            <strong>{money(fare)}</strong>
          </div>
          <p className="fine-print">{fareNote(j)}</p>
        </Card>

        {!active && <TripRoute pickup={j.pickup} stops={j.stops} dropoff={j.dropoff} />}
      </div>
      {actions && <div className="footer-bar">{actions}</div>}

      <Sheet
        open={sheet === "navigate"}
        title={`Directions to ${placeParts(target).title}`}
        onClose={() => setSheet(null)}
      >
        <div className="directions">
          {directions(target, toDropoff ? j.stops : []).map((app) => (
            <a
              key={app.name}
              className="btn btn-secondary btn-block"
              href={app.url}
              target="_blank"
              rel="noreferrer"
            >
              {app.name}
            </a>
          ))}
        </div>
        {toDropoff && j.stops.length > 0 && (
          <p className="fine-print">
            Google Maps takes in the stops. Waze and Apple Maps go straight to the destination.
          </p>
        )}
        <Notice tone="info">
          Come back to this app when you can: while it's off screen your location isn't shared, so
          the passenger can't see you coming.
        </Notice>
      </Sheet>

      <Sheet open={sheet === "complete"} title="Complete the trip?" onClose={() => setSheet(null)}>
        <CompleteSheet
          job={j}
          busy={busy === "complete"}
          onComplete={(body) => step("complete", body)}
        />
      </Sheet>

      <Sheet open={sheet === "no-show"} title="Mark as a no-show?" onClose={() => setSheet(null)}>
        <p>
          Only once you've waited and tried calling {name}. They won't be charged, and you'll be
          free for the next job.
        </p>
        <Button variant="danger" loading={busy === "no-show"} onClick={() => step("no-show")}>
          Yes, they didn't show
        </Button>
        <Button variant="ghost" onClick={() => setSheet(null)}>
          Keep waiting
        </Button>
      </Sheet>
    </div>
  );
}

const STEPS = ["To pickup", "Arrived", "On board", "Done"];
const JOB_STEPS: Partial<Record<DriverJob["status"], number>> = {
  accepted: 0,
  arrived: 1,
  on_board: 2,
  completed: 4,
};

function fareNote(job: DriverJob): string {
  const done = job.status === "completed";
  if (job.paymentMethod === "card") {
    return done
      ? "Taken from their card."
      : job.isEstimate
        ? "Taken from their card at the end, from the meter."
        : "Taken from their card when you complete the trip.";
  }
  if (job.paymentMethod === "account") {
    return `Nothing to collect: it goes on ${job.accountName ? `${job.accountName}'s` : "the account's"} invoice.`;
  }
  if (done) return job.cashCollected ? "Collected in cash." : "To collect in cash.";
  return job.isEstimate
    ? "Collect it in cash at the end. The app works out the fare when you complete."
    : `Collect ${money(job.amountToCollectPence)} in cash at the end.`;
}

function CompleteSheet({
  job,
  busy,
  onComplete,
}: {
  job: DriverJob;
  busy: boolean;
  onComplete: (body: { cashCollected: boolean }) => void;
}) {
  // A quoted cash fare is known now: collect it, then complete in one go.
  if (job.paymentMethod === "cash" && !job.isEstimate) {
    return (
      <>
        <p className="collect">
          Collect <strong>{money(job.amountToCollectPence)}</strong> in cash from{" "}
          {job.customer.name}.
        </p>
        <Button loading={busy} onClick={() => onComplete({ cashCollected: true })}>
          Collected. Complete the trip
        </Button>
      </>
    );
  }
  return (
    <>
      <p>
        {job.paymentMethod === "cash"
          ? "The fare's worked out from the route, then you'll see what to collect."
          : job.paymentMethod === "card"
            ? job.isEstimate
              ? "The fare's worked out from the route and taken from their card."
              : `${money(job.pricePence)} is taken from their card.`
            : "It goes on the account's invoice."}
      </p>
      <Button loading={busy} onClick={() => onComplete({ cashCollected: false })}>
        Complete the trip
      </Button>
    </>
  );
}
